import React, { useState, useEffect, useCallback, useMemo } from "react";
import { APIResponse } from "services/HttpService";
import useTimerThreshold from "hooks/useTimerThreshold";
import { Instant } from "@js-joda/core";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { isDatasetSubType, isMultiValue } from "utils/utils";
import {
  ProductTemplateEntryType,
  useGetProductTemplateConfig,
  useGetProductTemplateData,
} from "api/api-hooks/product-templates-hooks";
import { toInstant } from "utils/time-utils";
import { Tooltip } from "primereact/tooltip";
import { ProgressSpinner } from "primereact/progressspinner";
import { Error } from "components/dashboard/product-template-table/ProductTemplateTable";
import { T } from "services/lang/LanguageService";
import {
  HighlightColor,
  HighlightRange,
  IProductTemplate,
  ITableDataEntry,
} from "../product-template-table/utils/interfaces";
import { promiseHandlerWithControlledColumns } from "../product-template-table/utils/utils";
import {
  AggregatedValue,
  defaultBgColor,
  expiredGradient,
  ITiledProductItemView,
} from "./TiledProductItemViewTypes";
import { formatTooltip, getAverageValue } from "./TiledProductItemViewUtils";
import "./TiledProductItemView.scss";
import DatasetDisplay from "../product-template-table/components/templates/dataset/DatasetDisplay";

export default function TiledProductItemView(props: ITiledProductItemView) {
  const [valuesArray, setValuesArray] = useState<AggregatedValue[]>([]);

  const params: ProductTemplateEntryType = {
    safeName: props.safeName,
    dateFrom: props.from,
    dateTo: props.to,
    filter: props.filter ?? "",
  };

  const { data: templateData, isLoading } = useGetProductTemplateData(params, {
    enabled: Boolean(props.safeName || props.from || props.to),
  });

  const { data: configData } = useGetProductTemplateConfig(
    props.safeName,
    props.safeName,
    props.hiddenColumns,
    props.onlyShownColumns,
    {
      usePromiseHandler: (res: APIResponse<IProductTemplate>) =>
        promiseHandlerWithControlledColumns(res, props.hiddenColumns, props.onlyShownColumns),
    }
  );

  const handleValueResolved = useCallback((id: string, value: string) => {
    setValuesArray(prev => {
      const existing = prev.find(v => v.id === id);
      if (existing && existing.value === value) return prev;
      if (existing) {
        return prev.map(v => (v.id === id ? { ...v, value } : v));
      }
      return [...prev, { id, value }];
    });
  }, []);

  if (isLoading)
    return (
      <div className="p-4">
        <ProgressSpinner className="p-d-block p-mx-auto" />
      </div>
    );

  if (!templateData || templateData.length === 0 || !configData)
    return (
      <div className="p-4">
        <Error text={T("error.no-data-found")} />
      </div>
    );

  const valueColumn = configData.data?.attributes.find(attr => attr.referenceName === props.valueColumn);

  if (!valueColumn)
    return (
      <div className="p-4">
        <Error text={`Missing column: ${props.valueColumn}`} />
      </div>
    );

  const { highlightColor, bgHighlightRanges } = valueColumn;

  return (
    <div className="tiled-product-item-view-container">
      {templateData.map((row, index) => (
        <DatasetValueTemplate
          key={row.id}
          configData={configData}
          row={row}
          index={index}
          componentProps={props}
          valuesArray={valuesArray}
          highlightColor={highlightColor}
          bgHighlightRanges={bgHighlightRanges}
          handleValueResolved={handleValueResolved}
        />
      ))}
    </div>
  );
}

const DatasetValueTemplate = ({
  row,
  index,
  configData,
  componentProps,
  valuesArray,
  highlightColor,
  bgHighlightRanges,
  handleValueResolved,
}: {
  row: ITableDataEntry;
  index: number;
  configData: IProductTemplate;
  componentProps: ITiledProductItemView;
  valuesArray: AggregatedValue[];
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  handleValueResolved: (id: string, value: string) => void;
}) => {
  const entry = componentProps.valueColumn ? row?.entries?.[componentProps.valueColumn] : undefined;
  const entryValue = entry?.value;

  let values;
  let setPointDatasets: string[] = [];
  if (isMultiValue(entryValue)) {
    if (isDatasetSubType(entryValue, entryValue.values)) {
      values = entryValue.values;
    }
  }

  const datasets = values ?? [];
  const setPointEntry = componentProps.setpointColumn && row?.entries?.[componentProps.setpointColumn]?.value;

  if (isMultiValue(setPointEntry)) {
    if (isDatasetSubType(setPointEntry, setPointEntry.values)) {
      setPointDatasets = setPointEntry.values.filter(v => v !== "");
    }
  } else if (setPointEntry !== "" && setPointEntry != null) {
    setPointDatasets = [setPointEntry as string];
  }

  const rowId = String(row.id ?? index);

  return (
    <DatasetValueDisplay
      id={rowId}
      index={index}
      row={row}
      componentProps={componentProps}
      datasets={datasets}
      decimalPlaces={componentProps.decimalPlaces}
      maxAge={componentProps.maxAge}
      updateFrequency={componentProps.updateFrequency}
      highlightColor={highlightColor}
      bgHighlightRanges={bgHighlightRanges}
      setPointDatasets={setPointDatasets}
      onValueResolved={handleValueResolved}
      configData={configData}
      valuesArray={valuesArray}
    />
  );
};

const DatasetValueDisplay = ({
  id,
  index,
  row,
  componentProps,
  datasets,
  decimalPlaces,
  maxAge,
  updateFrequency,
  highlightColor,
  bgHighlightRanges,
  setPointDatasets,
  configData,
  valuesArray,
  onHighlightColors,
  onValueResolved,
  onTimestampChange,
}: {
  id: string;
  index: number;
  row: ITableDataEntry;
  componentProps: ITiledProductItemView;
  datasets: string[] | null;
  decimalPlaces?: number;
  maxAge?: number;
  updateFrequency?: number;
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  setPointDatasets?: string[] | null;
  configData: IProductTemplate;
  valuesArray: AggregatedValue[];
  onHighlightColors?: (color?: string, bgColor?: string, colorEntireRow?: boolean) => void;
  onValueResolved?: (id: string, val: string) => void;
  onTimestampChange?: (timestamp: number) => void;
}) => {
  const safeDatasets = useMemo(() => (Array.isArray(datasets) ? datasets.filter(Boolean) : []), [datasets]);

  const { data, isLoading } = useGetRecentData(safeDatasets, {
    options: {
      enabled: safeDatasets.length > 0,
      refetchInterval: updateFrequency ? updateFrequency * 1000 : 5000,
    },
  });

  const average = useMemo(() => getAverageValue(data), [data]);
  const unit = data?.[0]?.unit;

  const key = data?.[0]?.data?.[0]?.key;
  const datasetTime = useMemo(() => (key ? Instant.parse(key).toEpochMilli() : null), [key]);

  const datasetTimestamp = toInstant(datasetTime)?.toEpochMilli() ?? 0;

  useEffect(() => {
    if (datasetTimestamp && onTimestampChange) {
      onTimestampChange(datasetTimestamp);
    }
  }, [datasetTimestamp, onTimestampChange]);

  const formatted =
    average != null && !Number.isNaN(average)
      ? `${average.toFixed(decimalPlaces ?? 2)}${unit ? ` ${unit}` : ""}`
      : "-";

  useEffect(() => {
    if (onValueResolved) onValueResolved(id, formatted);
  }, [id, formatted, onValueResolved]);

  const isInitiallyExpired =
    maxAge && datasetTimestamp > 0 ? Instant.now().toEpochMilli() - datasetTimestamp > maxAge * 1000 : false;

  const { isExpired: timerExpired, resetTimer } = useTimerThreshold(maxAge, datasetTimestamp);

  const isExpired = isInitiallyExpired || timerExpired;

  useEffect(() => {
    resetTimer(datasetTimestamp);
  }, [datasetTimestamp, resetTimer]);

  if (safeDatasets.length === 0 || isLoading) return null;

  const resolvedValue = valuesArray.find(v => v.id === id)?.value;

  const tooltip = componentProps.columnsToDisplayInTooltip?.length
    ? formatTooltip(
        row,
        configData,
        componentProps.columnsToDisplayInTooltip,
        componentProps.valueColumn,
        resolvedValue,
        isExpired
      )
    : `${T("label.row-number")} ${index + 1}`;

  return (
    <div
      id={`tiled-product-item-view-item-${index}`}
      key={id}
      style={{ backgroundColor: componentProps.defaultColor ?? defaultBgColor }}
      className="tiled-product-item-view-item"
    >
      <Tooltip target={`#tiled-product-item-view-item-${index}`} content={tooltip} position="top" />
      <DatasetDisplay
        identifiers={safeDatasets}
        value={average}
        useAverage
        unit={unit}
        decimalPlaces={decimalPlaces}
        time={null}
        maxAge={maxAge}
        highlightColor={highlightColor}
        bgHighlightRanges={bgHighlightRanges}
        onHighlightColors={onHighlightColors}
        hideValue
        setPointDatasets={setPointDatasets}
        customBackground={isExpired ? expiredGradient : (componentProps.defaultColor ?? defaultBgColor)}
      />
    </div>
  );
};
